import { Employee } from '../types/employee.ts';
import { Contractor } from '../types/contractor.ts';

export interface RateCalculationInputs {
  hourlyWage: number;
  insurance: number;
  vacation: number;
  carFuel?: number;
  markup?: number;
  customRateAdjustments?: Employee['customRateAdjustments'];
}

export interface ContractorRateConfig {
  markupType: 'fixed' | 'percentage';
  markupValue: number;
}

/**
 * Calculate the total custom adjustments per hour
 */
export const calculateCustomAdjustments = (
  customRateAdjustments?: Employee['customRateAdjustments']
): number => {
  if (!customRateAdjustments) return 0;
  
  return Object.values(customRateAdjustments)
    .filter(adjustment => adjustment.isActive)
    .reduce((total, adjustment) => {
      if (adjustment.type === 'hourly') {
        return total + adjustment.amount;
      } else if (adjustment.type === 'daily') {
        // Assume 8 hours per day
        return total + (adjustment.amount / 8);
      } else if (adjustment.type === 'weekly') {
        // Assume 40 hours per week
        return total + (adjustment.amount / 40);
      }
      return total;
    }, 0);
};

/**
 * Calculate burdened rate using fixed markup
 */
export const calculateFixedMarkupRate = (inputs: RateCalculationInputs): number => {
  const fica = inputs.hourlyWage * 0.08;
  const customAdjustments = calculateCustomAdjustments(inputs.customRateAdjustments);
  const carFuel = inputs.carFuel || 0;
  const markup = inputs.markup || 0;
  
  return inputs.hourlyWage + fica + inputs.insurance + inputs.vacation + carFuel + customAdjustments + markup;
};

/**
 * Calculate burdened rate using percentage markup
 * Percentage Markup = Wage * (1 + percentageMarkup)
 */
export const calculatePercentageMarkupRate = (
  hourlyWage: number,
  markupPercentage: number
): number => {
  return hourlyWage * (1 + (markupPercentage / 100));
};

/**
 * Calculate burdened rate based on contractor configuration
 */
export const calculateBurdenedRate = (
  inputs: RateCalculationInputs,
  contractorConfig?: ContractorRateConfig
): number => {
  if (contractorConfig && contractorConfig.markupType === 'percentage') {
    // Percentage Markup = Wage * (1 + percentageMarkup)
    return calculatePercentageMarkupRate(inputs.hourlyWage, contractorConfig.markupValue);
  } else {
    // Fixed Markup = Wage + FICA + Insurance + Vacation + CarFuel + Markup + ContractorMarkup
    const baseRate = calculateFixedMarkupRate(inputs);
    const contractorMarkup = contractorConfig?.markupValue || 0;
    return baseRate + contractorMarkup;
  }
};

/**
 * Calculate contractor-specific rates from contractor markup configuration
 */
export const calculateContractorRates = (
  employee: Partial<Employee>,
  contractors: { [contractorId: string]: Contractor }
): {
  cecRate?: number;
  reliableRate?: number;
  btcRate?: number;
} => {
  const baseInputs: RateCalculationInputs = {
    hourlyWage: employee.hourlyWage || 0,
    insurance: employee.insurance || 0,
    vacation: employee.vacation || 0,
    carFuel: employee.carFuel || 0,
    markup: employee.markup || 0,
    customRateAdjustments: employee.customRateAdjustments
  };

  const rates: any = {};

  // Find contractors by name/prefix and calculate their rates
  Object.values(contractors).forEach(contractor => {
    const contractorName = contractor.companyName.toLowerCase();
    const contractorConfig: ContractorRateConfig = {
      markupType: contractor.billing.markupType,
      markupValue: contractor.billing.markupValue
    };

    if (contractorName.includes('cec') || contractorName.includes('commonwealth')) {
      rates.cecRate = calculateBurdenedRate(baseInputs, contractorConfig);
    } else if (contractorName.includes('reliable')) {
      rates.reliableRate = calculateBurdenedRate(baseInputs, contractorConfig);
    } else if (contractorName.includes('btc') || contractorName.includes('blue')) {
      rates.btcRate = calculateBurdenedRate(baseInputs, contractorConfig);
    }
  });

  // Fallback to default calculation if contractors not found
  const defaultRate = calculateFixedMarkupRate(baseInputs);
  return {
    cecRate: rates.cecRate || defaultRate,
    reliableRate: rates.reliableRate || defaultRate,
    btcRate: rates.btcRate || defaultRate
  };
};

/**
 * Get skills rating display text
 */
export const getSkillRatingText = (rating?: number): string => {
  if (!rating) return 'Not Rated';
  
  const ratings = {
    1: 'Beginner',
    2: 'Basic',
    3: 'Intermediate',
    4: 'Advanced', 
    5: 'Expert'
  };
  
  return ratings[rating as keyof typeof ratings] || 'Unknown';
};

/**
 * Validate skills ratings (1-5 scale)
 */
export const validateSkillsRatings = (skills?: Employee['skills']): boolean => {
  if (!skills) return true;
  
  return Object.values(skills).every(rating => 
    rating === undefined || (rating >= 1 && rating <= 5)
  );
};